
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ContentItem } from "@/components/dashboard/content-card/types";
import { checkExtensionInstalled, requestExtensionReWebClip } from "@/utils/extensionUtils";

interface ReWebClipResult {
  success: boolean;
  preservedManualThumbnail?: boolean;
  thumbnailUpdated?: boolean;
  updatedFields?: string[];
  error?: any;
  errorDetails?: any;
  statusCode?: number;
  usedExtension?: boolean;
}

// Type for the metadata in content_items
interface ContentMetadata {
  thumbnail_source?: string;
  last_extracted?: string;
  extraction_status?: string;
  extraction_method?: string;
  [key: string]: any;
}

// New interface for batch processing status
interface BatchProcessingStatus {
  total: number;
  processed: number;
  successful: number;
  failed: number;
  inProgress: boolean;
  cancelRequested: boolean;
}

// New type for clip levels
export type ClipLevel = 'soft' | 'mid' | 'hard';

export const useReWebClipContent = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isExtensionAvailable, setIsExtensionAvailable] = useState<boolean | null>(null);
  const [batchStatus, setBatchStatus] = useState<BatchProcessingStatus>({
    total: 0,
    processed: 0,
    successful: 0,
    failed: 0,
    inProgress: false,
    cancelRequested: false
  });
  
  // Check if extension is available on mount
  useEffect(() => {
    const checkExtension = async () => {
      try {
        const isAvailable = await checkExtensionInstalled();
        console.log("Clustr extension available:", isAvailable);
        setIsExtensionAvailable(isAvailable);
      } catch (error) {
        console.error("Error checking extension availability:", error);
        setIsExtensionAvailable(false);
      }
    };
    
    checkExtension();
  }, []);

  const reWebClipContent = async (
    contentId: string, 
    clipLevel: ClipLevel = 'soft'
  ): Promise<ReWebClipResult> => {
    if (isProcessing || !contentId) {
      console.log("Already processing content or missing content ID");
      return { success: false, error: "Already processing" };
    }

    setIsProcessing(true);
    console.log(`Starting ${clipLevel} re-web-clip for content:`, contentId);

    try {
      // First, get the content URL and current metadata
      const { data: contentItem, error: fetchError } = await supabase
        .from('content_items')
        .select('url, thumbnail_url, metadata, title, description')
        .eq('id', contentId)
        .single();
        
      if (fetchError || !contentItem) {
        console.error("Error fetching content URL:", fetchError);
        toast.error("Failed to fetch content URL");
        return { success: false, error: fetchError };
      }
      
      const url = contentItem.url;
      const metadata = contentItem.metadata as ContentMetadata | null;
      const hasManualThumbnail = metadata?.thumbnail_source === 'manual';
      
      // Try using extension if available
      if (isExtensionAvailable) {
        console.log(`Using browser extension for ${clipLevel} re-web-clipping:`, url);
        
        try {
          const extensionResult = await requestExtensionReWebClip(url);
          
          if (extensionResult.success && extensionResult.metadata) {
            // Extension successfully extracted metadata
            console.log("Extension extraction successful:", extensionResult);
            
            // Prepare update data based on clip level
            const updateData: Record<string, any> = {};
            
            // Handle different clip levels
            switch (clipLevel) {
              case 'soft':
                // Only update fields that are missing/empty
                if (extensionResult.metadata.title && !contentItem.title) {
                  updateData.title = extensionResult.metadata.title;
                }
                if (extensionResult.metadata.description && !contentItem.description) {
                  updateData.description = extensionResult.metadata.description;
                }
                if (extensionResult.metadata.thumbnailUrl && !contentItem.thumbnail_url) {
                  updateData.thumbnail_url = extensionResult.metadata.thumbnailUrl;
                }
                break;
                
              case 'mid':
                // Update all fields except manual thumbnails
                if (extensionResult.metadata.title) {
                  updateData.title = extensionResult.metadata.title;
                }
                if (extensionResult.metadata.description) {
                  updateData.description = extensionResult.metadata.description;
                }
                if (extensionResult.metadata.thumbnailUrl && !hasManualThumbnail) {
                  updateData.thumbnail_url = extensionResult.metadata.thumbnailUrl;
                }
                break;
                
              case 'hard':
                // Update everything including manual thumbnails
                if (extensionResult.metadata.title) {
                  updateData.title = extensionResult.metadata.title;
                }
                if (extensionResult.metadata.description) {
                  updateData.description = extensionResult.metadata.description;
                }
                if (extensionResult.metadata.thumbnailUrl) {
                  updateData.thumbnail_url = extensionResult.metadata.thumbnailUrl;
                }
                break;
            }
            
            if (extensionResult.metadata.contentType) {
              updateData.content_type = extensionResult.metadata.contentType;
            }
            
            // Update metadata
            const newMetadata = {
              ...(metadata || {}),
              last_extracted: new Date().toISOString(),
              extraction_status: 'completed',
              extraction_method: 'extension',
              clip_level: clipLevel
            };
            
            updateData.metadata = newMetadata;
            
            // Update content item in database if we have changes
            if (Object.keys(updateData).length > 1) { // More than just metadata
              const { error: updateError } = await supabase
                .from('content_items')
                .update(updateData)
                .eq('id', contentId);
                
              if (updateError) {
                console.error("Error updating content with extension data:", updateError);
                return { success: false, error: updateError };
              }
            }
            
            return {
              success: true,
              preservedManualThumbnail: hasManualThumbnail && clipLevel !== 'hard',
              thumbnailUpdated: !!updateData.thumbnail_url,
              updatedFields: Object.keys(updateData),
              usedExtension: true
            };
          } else if (extensionResult.error) {
            console.log("Extension extraction failed with error:", extensionResult.error);
            return {
              success: false,
              error: extensionResult.error,
              usedExtension: true,
              statusCode: 403
            };
          } else {
            console.log("Extension extraction failed, falling back to edge function");
            // Fall through to edge function approach
          }
        } catch (extensionError) {
          console.error("Extension extraction error, falling back to edge function:", extensionError);
          // Fall through to edge function approach
        }
      }

      // Use the extract-content-metadata function
      const { data, error } = await supabase.functions.invoke("extract-content-metadata", {
        body: { 
          contentId,
          clipLevel // Pass the clip level to the edge function
        }
      });

      if (error) {
        console.error("Error re-web-clipping content:", error);
        return { success: false, error };
      }

      console.log(`${clipLevel} re-web-clip result:`, data);

      if (data.success) {
        return { 
          success: true, 
          preservedManualThumbnail: data.preserved_manual_thumbnail,
          thumbnailUpdated: data.thumbnail_updated,
          updatedFields: data.updated_fields
        };
      } else {
        console.error(`${clipLevel} re-web-clip failed:`, data);
        
        return { 
          success: false, 
          error: data.error || "Unknown error",
          errorDetails: data.error_details,
          statusCode: data.status_code 
        };
      }
    } catch (error: any) {
      console.error(`Error in ${clipLevel} re-web-clip process:`, error);
      return { success: false, error };
    } finally {
      setIsProcessing(false);
    }
  };

  // Updated batch processing function with clip level support
  const batchReWebClipContent = async (
    contentIds: string[],
    clipLevel: ClipLevel = 'soft'
  ): Promise<{
    totalProcessed: number;
    successful: number;
    failed: number;
  }> => {
    if (batchStatus.inProgress) {
      toast.error("A batch operation is already in progress");
      return { totalProcessed: 0, successful: 0, failed: 0 };
    }

    const total = contentIds.length;
    if (total === 0) {
      toast.info("No items selected for re-clipping");
      return { totalProcessed: 0, successful: 0, failed: 0 };
    }

    // Initialize batch status
    setBatchStatus({
      total,
      processed: 0,
      successful: 0,
      failed: 0,
      inProgress: true,
      cancelRequested: false
    });

    const clipLevelName = clipLevel === 'soft' ? 'Soft' : clipLevel === 'mid' ? 'Mid' : 'Hard';
    toast.info(`Starting ${clipLevelName} Re-Clip for ${total} items...`);
    
    let successful = 0;
    let failed = 0;
    
    // Process items sequentially to avoid overwhelming the server
    for (let i = 0; i < contentIds.length; i++) {
      const contentId = contentIds[i];
      
      // Check if cancellation was requested
      if (batchStatus.cancelRequested) {
        toast.info("Batch operation cancelled");
        break;
      }
      
      try {
        // Update progress
        setBatchStatus(prev => ({
          ...prev,
          processed: i,
        }));
        
        toast.info(`${clipLevelName} Re-Clipping item ${i + 1} of ${total}`);
        
        // Re-clip the content with specified level
        const result = await reWebClipContent(contentId, clipLevel);
        
        if (result.success) {
          successful++;
          setBatchStatus(prev => ({ ...prev, successful: prev.successful + 1 }));
        } else {
          failed++;
          setBatchStatus(prev => ({ ...prev, failed: prev.failed + 1 }));
          console.error(`Failed to re-clip item ${contentId}:`, result.error);
        }
      } catch (error) {
        failed++;
        setBatchStatus(prev => ({ ...prev, failed: prev.failed + 1 }));
        console.error(`Error processing item ${contentId}:`, error);
      }
      
      // Small delay to avoid overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // Update final status
    setBatchStatus(prev => ({
      ...prev,
      processed: contentIds.length,
      inProgress: false,
      cancelRequested: false
    }));
    
    const result = {
      totalProcessed: successful + failed,
      successful,
      failed
    };
    
    // Show completion toast
    if (successful > 0 && failed === 0) {
      toast.success(`Successfully ${clipLevelName} Re-Clipped ${successful} items`);
    } else if (successful > 0 && failed > 0) {
      toast.info(`${clipLevelName} Re-Clipped ${successful} items, ${failed} failed`);
    } else if (successful === 0 && failed > 0) {
      toast.error(`Failed to ${clipLevelName} Re-Clip ${failed} items`);
    }
    
    return result;
  };

  // Cancel batch operation
  const cancelBatchOperation = () => {
    if (batchStatus.inProgress) {
      setBatchStatus(prev => ({ ...prev, cancelRequested: true }));
      toast.info("Cancelling batch operation after current item completes...");
      return true;
    }
    return false;
  };

  return {
    isProcessing,
    reWebClipContent,
    isExtensionAvailable,
    // New batch operations with clip level support
    batchReWebClipContent,
    batchStatus,
    cancelBatchOperation
  };
};
